import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "mdm-exit-intent-shown";

export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const armed = useRef(false);

  // Arm the trigger after a short delay so it doesn't fire on the very first navigation event
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    const armTimer = setTimeout(() => {
      armed.current = true;
    }, 8000);

    const onMouseLeave = (e) => {
      if (!armed.current) return;
      // Only trigger when the pointer moves above the viewport (likely heading to tab/close)
      if (e.clientY <= 0 && !sessionStorage.getItem(STORAGE_KEY)) {
        sessionStorage.setItem(STORAGE_KEY, "1");
        setOpen(true);
      }
    };

    // Mobile fallback: trigger after 25s of activity if not already shown
    const mobileTimer = setTimeout(() => {
      if (!sessionStorage.getItem(STORAGE_KEY)) {
        sessionStorage.setItem(STORAGE_KEY, "1");
        setOpen(true);
      }
    }, 35000);

    document.addEventListener("mouseleave", onMouseLeave);
    return () => {
      clearTimeout(armTimer);
      clearTimeout(mobileTimer);
      document.removeEventListener("mouseleave", onMouseLeave);
    };
  }, []);

  const downloadExtract = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/extract/first-chapter`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Metodo-di-Mare-Estratto-Capitolo-1.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Estratto scaricato. Buona lettura.");
      setOpen(false);
    } catch (err) {
      toast.error("Errore nel download. Riprova.");
    } finally {
      setDownloading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email, consent: true });
      toast.success("Iscritto. Sto preparando il PDF...");
      await downloadExtract();
      setEmail("");
    } catch (err) {
      toast.error("Email non valida. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0B192C]/70 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="exit-intent-overlay"
        >
          <motion.div
            initial={{ scale: 0.92, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 20, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-[#F9F8F6] border border-[#0B192C] max-w-2xl w-full overflow-hidden grid md:grid-cols-12 shadow-[0_30px_80px_-15px_rgba(11,25,44,0.4)]"
            data-testid="exit-intent-popup"
          >
            {/* Left visual */}
            <div className="hidden md:flex md:col-span-5 relative bg-gradient-to-br from-[#0B192C] via-[#1E3557] to-[#0E7C8F] p-8 items-end overflow-hidden">
              <div
                aria-hidden
                className="absolute -top-10 -right-10 w-40 h-40 opacity-40 blur-2xl"
                style={{
                  background:
                    "radial-gradient(circle, #E89B6C 0%, transparent 70%)",
                }}
              />
              <div className="relative">
                <p className="font-serif-display text-[80px] leading-[0.85] text-[#F9F8F6]">
                  01
                </p>
                <p className="overline text-[#E89B6C] mt-3">Capitolo 1</p>
                <p className="font-serif-display italic text-2xl text-[#F9F8F6] mt-2 leading-tight">
                  "La mia storia"
                </p>
              </div>
            </div>

            {/* Right content */}
            <div className="md:col-span-7 p-7 md:p-9 relative">
              <button
                onClick={() => setOpen(false)}
                aria-label="Chiudi"
                data-testid="exit-intent-close"
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-[#0B192C]/60 hover:text-[#0B192C]"
              >
                <X size={18} />
              </button>

              <p className="overline">Prima che tu vada</p>
              <h3 className="font-serif-display text-3xl md:text-4xl mt-2 leading-[1.1] text-[#0B192C]">
                Porta con te il
                <br />
                <em className="text-[#E89B6C]">primo capitolo.</em>
              </h3>
              <p className="mt-4 text-base font-light leading-relaxed text-[#3A4F6B]">
                Un assaggio del metodo, gratis. Il sogno della libertà,
                l'Amerigo Vespucci e la nascita del Metodo di Mare —
                direttamente in PDF.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-3">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="La tua email"
                  data-testid="exit-intent-email"
                  className="w-full bg-transparent border-0 border-b border-[#0B192C] py-3 text-base font-light text-[#0B192C] placeholder:text-[#3A4F6B]/50 focus:outline-none focus:border-b-2"
                />
                <p className="text-[10px] font-light leading-relaxed text-[#3A4F6B]">
                  Inviando accetti l'<a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline text-[#0E7C8F]">informativa privacy</a>. Puoi revocare il consenso quando vuoi.
                </p>
                <button
                  type="submit"
                  disabled={loading || downloading}
                  data-testid="exit-intent-submit"
                  className="group w-full inline-flex items-center justify-center gap-3 px-7 py-3.5 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.2em] hover:bg-[#1E3557] transition-all disabled:opacity-60"
                >
                  {loading || downloading ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Preparazione...
                    </>
                  ) : (
                    <>
                      Ricevi & scarica PDF
                      <ArrowRight
                        size={14}
                        className="transition-transform group-hover:translate-x-1"
                      />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-5 flex items-center gap-3 justify-between">
                <button
                  onClick={downloadExtract}
                  disabled={downloading}
                  data-testid="exit-intent-direct-download"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0B192C] link-underline disabled:opacity-60"
                >
                  <Download size={12} />
                  Scarica senza email
                </button>
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B]/70">
                  PDF · 11 pagine
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
